import { CompletionReq, CompletionRes } from './chat';


// api - agent create
export type AgentCreateArgs = Pick<
  AgentConfig,
  'description' | 'name' | 'steps'
> & Partial<Pick<AgentConfig, 'context'>>;

export type AgentCreateApiReq = Pick<
  AgentConfig<string, string>,
  'description' | 'name' | 'steps'
> & Partial<Pick<AgentConfig<string, string>, 'context'>>;

export type AgentCreateApiRes = AgentVersion;

// api - agent delete
export type AgentDeleteArgs = {
  agentId: string;
};

export type AgentDeleteApiRes = {
  agentId: string;
};

// api - agent config get
export type AgentConfigGetArgs = {
  agentId: string;
  version: number;
};

export type AgentConfigGetApiRes = AgentConfig<string, string>;

// api - agent invoke
export type AgentInvokeUrlOptions = {
  debug?: boolean;
  full?: boolean;
};

export type AgentInvokeLowCodeArgs = {
  agentId: string;
  version: string;
} & AgentInvokeApiReq;

export type AgentInvokeArgs = AgentInvokeApiReq;

export type AgentInvokeApiReq = {
  debug?: boolean;
} & CompletionReq;

export type AgentInvokeApiRes = CompletionRes[];

export type AgentInvokeStreamMessage = {
  content?: unknown;
  message_id?: string;
};

export type AgentInvokeStreamChunk = {
  correlation_id?: string;
  type?: string;
  errorMessage?: string;
  failureMessage?: string;
  messages?: AgentInvokeStreamMessage[];
  response_id?: string;
};

// api - agent update
export type AgentUpdateArgs = {
  agentId: string;
} & Partial<
  Pick<
    AgentConfig<string>,
    'editable' | 'isPublic'
  >
>;

export type AgentUpdateApiReq = Partial<
  Pick<
    AgentConfig<string>,
    'editable' | 'isPublic'
  >
>;

export type AgentUpdateApiRes = AgentVersion;

// api - agent version update
export type AgentVersionUpdateArgs = {
  agentId: string;
  version: string;
} & Pick<AgentConfig, 'context' | 'description' | 'name' | 'steps'>;

export type AgentVersionUpdateApiReq = Pick<
  AgentConfig<string, string>,
  'context' | 'description' | 'name' | 'steps'
>;

export type AgentVersionUpdateApiRes = AgentVersion;

// api - agents get
export type AgentsGetApiRes = {
  agents: BaseAgent[];
};

export enum AgentTypeEnum {
  LOW_CODE = 'low_code',
  AIDP = 'aidp'
}

export type BaseAgent<T = AgentAidp | AgentLowcode> = {
  data: T;
  description: string;
  editable: boolean;
  id: string;
  name?: string;
  constellationName?: string;
  version?: string;
  isPublic: boolean;
  type: AgentTypeEnum;
};

export type AgentAidp = {
  host: string;
  port: number;
  agentId?: string;
  agentName?: string;
  constellationName?: string;
  version?: string; // TODO: [jh 2025-05-01] revisit when mas agents have relevant props
};

export type AgentLowcode = {
  agentId: string;
  versions: {
    [key: string]: AgentVersion;
  };
  deploymentStatus?: DeploymentStatus;
};

// local
export type AgentVersion = {
  agentId: string;
  agentConfigId: string;
  agentVersionId: string;
  createdAt: string;
  description: string;
  editable: boolean;
  isPublic: boolean;
  name: string;
  version: number;
};

export type AgentConfig<T = AgentConfigStep[], U = AgentConfigGlobalContexts> = {
  steps: T;
  url: string;
  versions: string[];
  context: U;
} & AgentVersion;

export enum AgentConfigStepRouteOutputTypeEnum {
  FROM_ACTION = 'from_action',
  FROM_INPUT = 'from_input'
}

export type AgentConfigStepRouteOutputs = {
  [key: string]: {
    key: string;
    type: AgentConfigStepRouteOutputTypeEnum;
  };
};

export type AgentConfigStepRoute = {
  outputs: AgentConfigStepRouteOutputs;
  step?: string;
};

export enum AgentConfigStepTypeEnum {
  LLM = 'llm',
  API = 'api',
  VECTORDB = 'vectordb',
  START = 'start',
  END = 'end',
  AGENT = 'agent'
}

export type AgentConfigStepApiActionHeaders = {
  header: string;
  value: string;
};

export type AgentConfigStepAgent = {
  agent_id?: string;
  agent_name?: string;
  agent_type?: AgentTypeEnum;
  agent_version?: number;
  context_values: string;
  messages_key?: string;
  conversation_key?: string;
  include_full_message?: boolean;
};

export type AgentConfigStepApiAction = {
  body: string;
  conversation_key?: string;
  url: string;
  headers?: AgentConfigStepApiActionHeaders[];
  method: string;
};

export type AgentConfigStepLLMActionTool = {
  type: string;
  function: {
    description: string;
    name: string;
    parameters: {
      type: string;
      properties: object;
    };
  };
};
export type AgentConfigStepLLMActionMCPClient = {
  name: string;
  url: string;
  token: string;
};

export type AgentConfigStepLLMAction = {
  conversation_key: string;
  model: string;
  temperature: number;
  prompt: string;
  tools?: AgentConfigStepLLMActionTool[];
  mcp_servers?: AgentConfigStepLLMActionMCPClient[];
};

export type AgentConfigStepVectorDBAction = {
  conversation_key?: string;
  index: string;
  query: string;
};
export type BaseAction = Record<string, unknown>;
export type AgentConfigStepAction = (
  | AgentConfigStepApiAction
  | AgentConfigStepLLMAction
  | AgentConfigStepVectorDBAction
  | AgentConfigStepAgent
) & { tools?: AgentConfigStepLLMActionTool[] } & { mcp_servers?: AgentConfigStepLLMActionMCPClient[] }; // TODO: hack to use one action change handler

export type ContextInputType = 'STRING' | 'INTEGER' | 'ARRAY' | 'OBJECT';

export const CONTEXT_INPUT_TYPES: Record<ContextInputType, string> = {
  STRING: 'String',
  INTEGER: 'Integer',
  ARRAY: 'Array',
  OBJECT: 'Object'
};

export type AgentConfigStepInputs = {
  [ key: string ]: {
    type: string;
  };
};

export type AgentConfigGlobalContexts = {
  [ key: string ]: {
    type: ContextInputType;
  };
};

export type AgentConfigStepRoutes = {
  [ key: string ]: AgentConfigStepRoute;
};

export enum AgentConfigStepLabelEnum {
  END = 'end',
  START = 'start'
}

export type AgentConfigStep<T = AgentConfigStepAction> = {
  action?: T;
  description: string;
  id: string;
  inputs: AgentConfigStepInputs;
  label?: AgentConfigStepLabelEnum;
  position?: {
    x: number;
    y: number;
  };
  routes: AgentConfigStepRoutes;
  type: AgentConfigStepTypeEnum;
};

export enum AgentActionEnum {
  DELETE = 'delete',
  PUBLIC = 'public',
  EDITABLE = 'editable',
  TURN_OFF = 'turn_off',
  TURN_ON = 'turn_on',
  RESTART = 'restart'
}

// Matches AgentStatusDto from aidp-core
export enum DeploymentStatus {
  DEPLOYING = 'DEPLOYING',
  SCHEDULING = 'SCHEDULING',
  STARTING = 'STARTING',
  RESTARTING = 'RESTARTING',
  FAILED_DEPLOYMENT = 'FAILED_DEPLOYMENT',
  RUNNING = 'RUNNING',
  ERRORED = 'ERRORED',
  DEGRADED = 'DEGRADED',
  OFF = 'OFF'
}

// Matches AutoScalingConfigDto from aidp-core (present when auto-scaling is enabled)
export type AutoScalingConfigDto = {
  enabled: boolean;
  minReplicas: number;
  maxReplicas: number;
  targetCpuUtilization?: number;
  targetMemoryUtilization?: number;
};

// Matches AutoScalingStatusDto from aidp-core (present when auto-scaling enabled and replica counts known)
export type AutoScalingStatusDto = {
  currentReplicas: number;
  desiredReplicas: number;
  lastScaleTime?: string;
};

// Matches ReplicaStatusDto from aidp-core (present when manual scaling and replica counts known)
export type ReplicaStatusDto = {
  currentReplicas: number;
  desiredReplicas: number;
};

// Matches AgentDto from aidp-core GET /v2/agents/{id}
export type ByoaAgent = {
  id?: string;
  name: string;
  status: DeploymentStatus;
  createdAt: string;
  updatedAt: string;
  error?: string;
  replicas?: number;
  autoScalingConfig?: AutoScalingConfigDto;
  autoScalingStatus?: AutoScalingStatusDto;
  replicaStatus?: ReplicaStatusDto;
  version?: number;
  versionExternalId?: string;
  environmentVariables?: Record<string, string>;
  envToSecretName?: Record<string, string>;
};

export type ByoaAgentsGetApiRes = {
  agents: ByoaAgent[];
};


// api - agent scale
export type AgentScaleArgs = {
  agentId: string;
  autoScaling: boolean;
  instances?: number;
  minInstances?: number;
  maxInstances?: number;
  size: 'Small' | 'Medium' | 'Large';
  targetCpuUtilization?: number;
  targetMemoryUtilization?: number;
};

export type AgentScaleApiReq = Omit<AgentScaleArgs, 'agentId'>;

export type AgentScaleApiRes = {
  agentId: string;
  status: string;
};

// api - agent restart
export type AgentRestartArgs = {
  agentId: string;
};

export type AgentRestartApiRes = {
  agentId: string;
  status: string;
};

// api - agent turn off
export type AgentTurnOffArgs = {
  agentId: string;
};

export type AgentTurnOffApiRes = {
  agentId: string;
  status: string;
};
